import { NL_WKR_CATEGORIES, type NlWkrCategory } from '@releve/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNlWkrEnabled } from '@/hooks/useNlWkr';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Per-line WKR (Werkkostenregeling) category select — the booking-flow
 * touchpoint of the nl_wkr subsystem (docs/TAX.md "WKR (NL)").
 *
 * SELF-GATING: renders null unless the `nl_wkr` flag is on AND the org's tax
 * jurisdiction is NL (useNlWkrEnabled; held out of the tree while the flag is
 * loading — BudgetCategoryPicker precedent). Call sites mount it
 * unconditionally behind their own opt-in prop and never need gate logic.
 *
 * Deliberately form-agnostic (controlled value/onChange, no react-hook-form
 * coupling) so the same component serves InvoiceLineItems, the manual-journal
 * modal, the bank-feed categorize cell, and the split dialog.
 */

// Radix Select forbids empty-string item values; map "no category" through a
// sentinel so callers deal only in NlWkrCategory | null.
const NONE_VALUE = '__none__';

interface WkrCategorySelectProps {
  value: NlWkrCategory | null | undefined;
  onChange: (category: NlWkrCategory | null) => void;
  disabled?: boolean;
  /** Compact trigger for tight grid cells (bank feed); default matches form cells. */
  size?: 'default' | 'compact';
}

export function WkrCategorySelect({ value, onChange, disabled, size = 'default' }: WkrCategorySelectProps) {
  const { t } = useTranslation();
  const { enabled, isLoading } = useNlWkrEnabled();

  if (!enabled || isLoading) return null;

  return (
    <Select
      value={value ?? NONE_VALUE}
      onValueChange={(next) => onChange(next === NONE_VALUE ? null : (next as NlWkrCategory))}
      disabled={disabled}
    >
      <SelectTrigger
        className={size === 'compact' ? 'h-7 text-xs' : 'h-9'}
        aria-label={t('tax.nl.wkr.selectLabel')}
      >
        <SelectValue placeholder={t('tax.nl.wkr.selectPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE} className="text-muted-foreground">
          {t('tax.nl.wkr.categories.none')}
        </SelectItem>
        {NL_WKR_CATEGORIES.map((category) => (
          <SelectItem key={category} value={category}>
            {t(`tax.nl.wkr.categories.${category}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
